import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "~/components/shared/Icon";
import Indicator from "~/components/shared/Indicator";
import { twMerge } from "tailwind-merge";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { useAnnotationsPanel } from "~/components/build/Show/lib/useAnnotationsPanel";
import { useDrawerReplacesAnnotations } from "~/components/build/Show/lib/useDrawerReplacesAnnotations";
import { withoutDrawerParams } from "~/components/build/Show/lib/urlForView";

export default function AnnotationsPanelToggle() {
  const { build } = useBuild();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const totalCount = getAnnotationCounts(build).totalCount;
  const { isOpen, toggle, setOpen } = useAnnotationsPanel();
  const drawerReplacesAnnotations = useDrawerReplacesAnnotations();

  // While a right-docked drawer occupies the annotations slot the panel is
  // hidden behind it, so the toggle reads as inactive regardless of the
  // panel's underlying open state.
  const active = isOpen && !drawerReplacesAnnotations;

  const handleClick = useCallback(() => {
    if (drawerReplacesAnnotations) {
      // The drawer is occupying the annotations slot. Make sure the panel is
      // open, then close the drawer so the panel can take the slot.
      setOpen(true);
      const qs = withoutDrawerParams(new URLSearchParams(search)).toString();
      navigate(qs ? `${pathname}?${qs}` : pathname);
    } else {
      toggle();
    }
  }, [drawerReplacesAnnotations, setOpen, toggle, navigate, pathname, search]);

  const errorCount =
    (build.annotationCountsByStyle?.error || 0) +
    (build.statistics?.jobAnnotationCountsByStyle?.error || 0);
  const hasErrors = errorCount > 0;

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="AnnotationsPanelToggle"
      aria-pressed={active}
      title={active ? "Close annotations panel" : "Open annotations panel"}
      className={twMerge(
        "relative inline-flex size-7 items-center justify-center rounded-sm shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.05),0_0_0_1px_rgba(0,0,0,0.08),0_1px_0_0_rgba(0,0,0,0.1)] transition-[background-color,box-shadow,color] duration-100",
        active
          ? "bg-purple-100 text-purple-600 hover:bg-purple-100"
          : "bg-white text-[#334155] hover:bg-gray-100",
      )}
    >
      <span className="relative inline-flex items-center">
        <Icon
          icon={active ? "custom/outline/panel-right-close" : "custom/outline/panel-right-open"}
          className="size-[18px]"
        />
        {!active && totalCount > 0 && (
          <Indicator
            animate={false}
            size="sm"
            color={hasErrors ? "red" : "purple"}
            className="absolute right-0 top-0 -translate-y-[calc(50%_-_1px)] translate-x-[calc(50%_-_2px)]"
          />
        )}
      </span>
    </button>
  );
}
